import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import {
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  Forum as FeedbackIcon,
  Group as PeopleIcon,
  LocationOn as LocationIcon,
  Sms as SmsIcon,
  Star as StarIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

/**
 * AppShell — mobile-first chrome shared across pages.
 *
 * Why: gives every page a consistent sticky top bar (with optional back button +
 * title + actions slot), a bottom tab nav on mobile (~44px+ targets, hides on md+),
 * and respects iOS safe areas. Heeds the UI wiki: predictable touch targets, no
 * entrance animation on chrome, motion under 300ms, focus-visible rings, tabular
 * nums baked in via index.css.
 */
const NAV = [
  { label: 'Dashboard',  to: '/',                     icon: <DashboardIcon />, match: (p) => p === '/' || (p.startsWith('/dashboard') && !p.includes('/feedback') && !p.includes('/manage') && !p.includes('/supervisor-')) },
  { label: 'Feedback',   to: '/dashboard/feedback',   icon: <FeedbackIcon />,  match: (p) => p.includes('/feedback') },
  { label: 'Supervisors',to: '/dashboard/supervisor-information', icon: <PeopleIcon />, match: (p) => p.includes('/supervisor-information') || p.includes('/manage-supervisors') },
];

export function BottomNav() {
  const { pathname } = useLocation();
  const { isAdmin } = useAdmin();
  const visibleNav = NAV.filter((item) => item.label === 'Dashboard' || isAdmin);
  return (
    <nav className="bottom-nav" aria-label="Primary">
      <div className="bottom-nav-inner">
        {visibleNav.map((item) => {
          const active = item.match(pathname);
          // "More" is a sheet trigger normally; here we just link home so the
          // user always has a way back. Real "more sheet" lives on the home page.
          return (
            <Link
              key={item.label}
              to={item.to}
              className={`bottom-nav-item${active ? ' is-active' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export default function AppShell({
  title,
  subtitle,
  back = false,
  actions = null,
  children,
  hideBottomNav = false,
  contentClassName = '',
}) {
  const navigate = useNavigate();
  return (
    <div className="shell">
      <header className="shell-topbar">
        <div className="shell-topbar-inner">
          {back ? (
            <button
              type="button"
              className="icon-btn"
              aria-label="Go back"
              onClick={() => (typeof back === 'function' ? back() : typeof back === 'string' ? navigate(back) : navigate(-1))}
            >
              <ArrowBackIcon />
            </button>
          ) : (
            <img
              src="/ixora_logo_clean.png"
              alt=""
              aria-hidden="true"
              style={{ height: 28, width: 'auto', borderRadius: 6 }}
            />
          )}
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div className="shell-topbar-title">{title || 'Ixora Dashboard'}</div>
            {subtitle && (
              <div
                style={{
                  fontFamily: "'Geist', sans-serif",
                  fontSize: 12,
                  color: 'var(--c-ink-3)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginTop: 1,
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
          {actions && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              {actions}
            </div>
          )}
        </div>
      </header>

      <main className={`shell-main ${contentClassName}`}>{children}</main>

      {!hideBottomNav && <BottomNav />}
    </div>
  );
}
